import { screenDefinitionSchema, type ScreenDefinition } from './definitions/screen';

export type FragmentLoader = (slotId: string) => Promise<ScreenDefinition[]>;

/** Registry of published screen fragments per extension slot (sync registrations + cached async loads). */
export interface FragmentStore {
  /** Registers fragments for a slot; returns an unregister function. */
  register(slotId: string, ...definitions: ScreenDefinition[]): () => void;
  get(slotId: string): ScreenDefinition[];
  has(slotId: string): boolean;
  /** Loads fragments through the loader once per slot (deduplicates in-flight calls). */
  load(slotId: string, loader?: FragmentLoader): Promise<ScreenDefinition[]>;
  isLoaded(slotId: string): boolean;
  setLoader(loader: FragmentLoader | null): void;
  subscribe(listener: () => void): () => void;
  /** Monotonic counter bumped on every change (stable snapshot for `useSyncExternalStore`). */
  getVersion(): number;
  clear(): void;
}

const EMPTY: ScreenDefinition[] = [];

export function createFragmentStore(initialLoader?: FragmentLoader): FragmentStore {
  const fragments = new Map<string, ScreenDefinition[]>();
  const loaded = new Set<string>();
  const inflight = new Map<string, Promise<ScreenDefinition[]>>();
  const listeners = new Set<() => void>();
  let loader: FragmentLoader | null = initialLoader ?? null;
  let version = 0;

  const notify = (): void => {
    version += 1;
    for (const l of listeners) l();
  };

  const store: FragmentStore = {
    register: (slotId, ...definitions) => {
      fragments.set(slotId, [...(fragments.get(slotId) ?? []), ...definitions]);
      notify();
      return () => {
        const current = fragments.get(slotId) ?? [];
        fragments.set(
          slotId,
          current.filter((d) => !definitions.includes(d)),
        );
        notify();
      };
    },
    get: (slotId) => fragments.get(slotId) ?? EMPTY,
    has: (slotId) => (fragments.get(slotId)?.length ?? 0) > 0,
    load: (slotId, override) => {
      const fn = override ?? loader;
      if (!fn || loaded.has(slotId)) return Promise.resolve(store.get(slotId));
      const pending = inflight.get(slotId);
      if (pending) return pending;
      const promise = fn(slotId)
        .then((defs) => {
          loaded.add(slotId);
          fragments.set(slotId, [...(fragments.get(slotId) ?? []), ...defs]);
          notify();
          return store.get(slotId);
        })
        .finally(() => inflight.delete(slotId));
      inflight.set(slotId, promise);
      return promise;
    },
    isLoaded: (slotId) => loaded.has(slotId),
    setLoader: (next) => {
      loader = next;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getVersion: () => version,
    clear: () => {
      fragments.clear();
      loaded.clear();
      inflight.clear();
      notify();
    },
  };
  return store;
}

/** Process-wide store used by `ExtensionSlot` when no provider supplies one. */
export const fragmentStore: FragmentStore = createFragmentStore();

/** Registers a fragment in the global store; returns an unregister function. */
export function registerFragment(slotId: string, definition: ScreenDefinition, store: FragmentStore = fragmentStore): () => void {
  return store.register(slotId, definition);
}

/** Artifact row as returned by `GET /api/v1/platform/metadata/artifacts?kind=SCREEN`. */
export interface MetadataArtifact {
  id?: string;
  key?: string;
  kind: string;
  status?: string;
  version?: number;
  content: unknown;
}

/** Groups published SCREEN artifacts by their `content.slot`; invalid or unpublished rows are skipped. */
export function fragmentsFromArtifacts(artifacts: MetadataArtifact[]): Record<string, ScreenDefinition[]> {
  const out: Record<string, ScreenDefinition[]> = {};
  for (const artifact of artifacts) {
    if (artifact.kind !== 'SCREEN') continue;
    if (artifact.status !== undefined && artifact.status !== 'PUBLISHED') continue;
    const parsed = screenDefinitionSchema.safeParse(artifact.content);
    if (!parsed.success || !parsed.data.slot) continue;
    (out[parsed.data.slot] ??= []).push(parsed.data);
  }
  return out;
}

/** Registers every published SCREEN artifact with a `slot` into the store. */
export function registerFragmentsFromArtifacts(artifacts: MetadataArtifact[], store: FragmentStore = fragmentStore): void {
  for (const [slotId, defs] of Object.entries(fragmentsFromArtifacts(artifacts))) store.register(slotId, ...defs);
}
